<script setup lang="ts">
  import { SERIES_COLORS } from "@/lib/chartPalettes";
  import { computed } from "vue";
  import ResultsLineChart from "@/components/Survey/ResultsV2/Charts/ResultsLineChart.vue";
  import type { MetricsTableData, MetricsTableRow } from "@stores/useCampaignsPivotStore";
  import { dimensionValue, shortTemporalLabel } from "../pivotDimensions";
  import { formatMetricTick, formatMetricValue, type PivotMetricDefinition } from "../pivotMetrics";
  import type { PivotColumn, PivotMetrics } from "@/types";

  // One metric read down the (temporal) rows: x = rows, one line per column
  // bucket — or a single line when the table has no column dimension.
  const props = defineProps<{ data: MetricsTableData; metric: PivotMetricDefinition }>();

  // Past this many lines a chart stops reading; the columns with the most respondents win.
  const MAX_SERIES = 8;

  // A bucket with no line for a counter means zero; for a rate it means no data.
  const isCounter = computed(() => ["integer", "currency_int"].includes(props.metric.format) && props.metric.key !== "n_per_survey");

  const rows = computed(() => props.data.rows.filter((r) => !r.isSpecial));

  const categories = computed(() =>
    rows.value.map((r) => props.data.rowDimensions.map((d) => shortTemporalLabel(d, dimensionValue(d, r.dims))).join(" · ")),
  );

  const columnLabel = (column: PivotColumn) => props.data.columnDimensions.map((d) => dimensionValue(d, column.dims)).join(" · ");

  const totalN = (column: PivotColumn) => rows.value.reduce((sum, row: MetricsTableRow) => sum + (row.cells[column.key]?.n ?? 0), 0);

  const rankedColumns = computed(() => props.data.columns.slice().sort((a, b) => totalN(b) - totalN(a)));
  const shownColumns = computed(() => rankedColumns.value.slice(0, MAX_SERIES));
  const hiddenCount = computed(() => rankedColumns.value.length - shownColumns.value.length);

  const point = (metrics: Partial<PivotMetrics> | undefined) => {
    const value = metrics?.[props.metric.key as keyof PivotMetrics];
    if (value == null) return isCounter.value ? { value: 0, n: null } : null;
    return { value: Number(value), n: metrics?.n ?? null };
  };

  const series = computed(() => {
    if (!props.data.columns.length) {
      return [{ id: "all", label: props.metric.label, color: SERIES_COLORS[0], points: rows.value.map((r) => point(r.metrics)) }];
    }
    return shownColumns.value.map((column, i) => ({
      id: column.key,
      label: columnLabel(column),
      color: SERIES_COLORS[i % SERIES_COLORS.length],
      points: rows.value.map((r) => point(r.cells[column.key])),
    }));
  });

  const format = (value: number) => formatMetricValue(props.metric, value);
  const tickFormat = (value: number) => formatMetricTick(props.metric, value);
</script>

<template>
  <div class="rounded-md border bg-white p-3 flex flex-col gap-2">
    <ResultsLineChart
      :series="series"
      :categories="categories"
      :format="format"
      :tick-format="tickFormat"
      :show-values="series.length === 1 && categories.length <= 12" />
    <div
      v-if="data.columns.length"
      class="flex flex-wrap gap-x-4 gap-y-1 px-1 text-xs">
      <span
        v-for="s in series"
        :key="s.id"
        class="inline-flex items-center gap-1.5 text-muted-foreground">
        <span
          class="size-2 shrink-0 rounded-full"
          :style="{ backgroundColor: s.color }" />
        <span class="truncate max-w-64">{{ s.label }}</span>
      </span>
      <span
        v-if="hiddenCount > 0"
        class="text-muted-foreground/70 italic">
        + {{ hiddenCount }} more column{{ hiddenCount > 1 ? "s" : "" }} not drawn (lowest N)
      </span>
    </div>
  </div>
</template>
